// @ts-nocheck
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getOrderState, OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Tooltip from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block/Block";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@store/Overrides/Components/AddressInfoDisplay";
import { orderProps } from "@store/Themes/common/props/order";
import constants from "@store/constants";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@store/MobiusOverrides/DatePicker/DatePicker";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@store/Overrides/Components/SmallHeadingAndText";
import { infoProps } from "@store/Themes/common/props/info";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import React, { FC, useContext, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const OrderDate = getWidgetComponent("OrderDetails/OrderDate");
const Notes = getWidgetComponent("OrderDetails/OrderDetailsNotes");
const Status = getWidgetComponent("OrderDetails/OrderDetailsStatus");
const Salesperson = getWidgetComponent("OrderDetails/OrderSalesperson");
const RequestedDate = getWidgetComponent("OrderDetails/OrderDetailsRequestedDate");
const Carrier = getWidgetComponent("OrderDetails/OrderDetailsCarrier");
const DeliveryTerms = getWidgetComponent("OrderDetails/OrderDetailsDeliveryTerms");
const PaymentTerms = getWidgetComponent("OrderDetails/OrderDetailsPaymentTerms");
const PoNumber = getWidgetComponent("OrderDetails/OrderPo");
const ShippingAddress = getWidgetComponent("OrderDetails/OrderDetailsShippingAddress");
const BillingAddress = getWidgetComponent("OrderDetails/OrderDetailsBillingAddress");
const VatNumber = getWidgetComponent("OrderDetails/OrderDetailsVatNumber");
const VmiLocation = getWidgetComponent("OrderDetails/OrderDetailsVmiLocation");

interface OwnProps extends WidgetProps {
    orderState?: ReturnType<typeof getOrderState>;
}
const mapStateToProps = (state: ApplicationState) => ({
    changeRequestOrder: state.greif.pages.orderChange.changeRequestOrder,
    newShipTo: state.greif.pages.orderChange.newShipTo,
    language: state.context.session.language,
});

export interface OrderDetailsInformationStyles {
    block?: BlockProps;
    orderNumberHeading?: TypographyProps;
    infoGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    infoLabel?: TypographyPresentationProps;
    infoValue?: TypographyPresentationProps;
    notesGridItem?: GridItemProps;
    shippingInformationTitle?: TypographyProps;
    billingInformationTitle?: TypographyProps;
    billingShippingGridItem?: GridItemProps;
    orderChangeDatePicker?: DatePickerPresentationProps;
    newInfoStyles?: SmallHeadingAndTextStyles;
    shipToAddressTitleText?: TypographyProps;
    shipToAddressDisplay?: AddressInfoDisplayStyles;
}

export const orderDetailsInformationStyles: OrderDetailsInformationStyles = {
    block: { ...orderProps.block, variantSide: "bottom" },
    orderNumberHeading: orderProps.subtitle,
    infoGridContainer: infoProps.grid?.container,
    infoGridItem: {
        width: [6, 3, 4, 2, 2],
        printWidth: 4,
        css: css`
            display: block;
        `,
    },
    notesGridItem: infoProps.grid?.fullItem,
    shippingInformationTitle: orderProps.subtitle,
    billingInformationTitle: orderProps.subtitle,

    billingShippingGridItem: {
        width: [12, 6, 6, 4, 4],
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    orderChangeDatePicker: {
        cssOverrides: {
            formInputWrapper: css`
                width: 70%;
            `,
        },
    },
    newInfoStyles: {
        heading: {
            ...infoProps.label,
        },
    },
    shipToAddressTitleText: infoProps.label,
};
type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const styles = orderDetailsInformationStyles;

const ReviewRequestOrderChangeInformation: FC<Props> = ({
    orderState: baseOrderState,
    changeRequestOrder,
    newShipTo,
    language,
}) => {
    const { orderSettings, productSettings } = useSettingsCollection();
    const orderStateContext = useContext(OrderStateContext);
    const orderState = baseOrderState || orderStateContext;
    const order = orderState.value;
    const { newPoNumber, newRequestedDate } = changeRequestOrder;

    if (!order) {
        return null;
    }

    const showPoNumber = orderSettings.showPoNumber && order.customerPO;
    const showVatNumber = productSettings.enableVat && order.customerVatNumber;
    const showTerms = orderSettings.showTermsCode && order.terms;
    const showInformations =
        order.orderDate ||
        showPoNumber ||
        order.statusDisplay ||
        order.salesperson ||
        showVatNumber ||
        showTerms ||
        order.vmiLocationName ||
        order.notes ||
        order.requestedDeliveryDateDisplay ||
        order.properties.carrier ||
        order.properties.deliveryTerms;

    const hasUpdatedOrderInformation = newRequestedDate || newPoNumber || (newShipTo && newShipTo.id);

    return (
        <OrderStateContext.Provider value={orderState}>
            <Block {...styles.block}>
                <Typography {...styles.orderNumberHeading}>
                    {translate("Greif Order Number: ")}
                    {order.erpOrderNumber ? order.erpOrderNumber : order.webOrderNumber}
                </Typography>
                {showInformations ? (
                    <GridContainer {...styles.infoGridContainer}>
                        {order.orderDate ? (
                            <GridItem {...styles.infoGridItem}>
                                <OrderDate />
                            </GridItem>
                        ) : null}
                        {showPoNumber && !newPoNumber ? (
                            <GridItem {...styles.infoGridItem}>
                                <PoNumber />
                            </GridItem>
                        ) : null}
                        {order.statusDisplay ? (
                            <GridItem {...styles.infoGridItem}>
                                <Status />
                            </GridItem>
                        ) : null}
                        {order.salesperson ? (
                            <GridItem {...styles.infoGridItem}>
                                <Salesperson />
                            </GridItem>
                        ) : null}
                        {showVatNumber ? (
                            <GridItem {...styles.infoGridItem}>
                                <VatNumber />
                            </GridItem>
                        ) : null}
                        {order.vmiLocationName ? (
                            <GridItem {...styles.infoGridItem}>
                                <VmiLocation />
                            </GridItem>
                        ) : null}
                        {order.requestedDeliveryDateDisplay && !newRequestedDate ? (
                            <GridItem {...styles.infoGridItem}>
                                <RequestedDate />
                            </GridItem>
                        ) : null}
                        {order.properties.carrier ? (
                            <GridItem {...styles.infoGridItem}>
                                <Carrier />
                            </GridItem>
                        ) : null}
                        {order.properties.deliveryTerms ? (
                            <GridItem {...styles.infoGridItem}>
                                <DeliveryTerms />
                            </GridItem>
                        ) : null}
                        {showTerms ? (
                            <GridItem {...styles.infoGridItem}>
                                <PaymentTerms />
                            </GridItem>
                        ) : null}
                        {order.notes ? (
                            <GridItem {...styles.notesGridItem}>
                                <Notes />
                            </GridItem>
                        ) : null}
                    </GridContainer>
                ) : null}
            </Block>
            <Block {...styles.block}>
                <GridContainer>
                    <GridItem {...styles.billingShippingGridItem}>
                        <BillingAddress />
                    </GridItem>
                    {newShipTo && !newShipTo.id && (
                        <GridItem {...styles.billingShippingGridItem}>
                            <ShippingAddress />
                        </GridItem>
                    )}
                </GridContainer>
            </Block>
            {hasUpdatedOrderInformation && (
                <Block {...styles.block}>
                    <Typography variant="h4" as="h4">
                        {translate("Edited Order Information")}
                    </Typography>
                    <GridContainer>
                        {newShipTo && newShipTo.id && (
                            <GridItem {...styles.billingShippingGridItem}>
                                <Typography {...styles.shipToAddressTitleText}>
                                    {translate("New Ship To Address")}
                                </Typography>
                                <AddressInfoDisplay
                                    companyName={newShipTo.companyName}
                                    address1={newShipTo.address1}
                                    address2={newShipTo.address2}
                                    address3={newShipTo.address3}
                                    // Greif Removed (address 4)
                                    city={newShipTo.city}
                                    state={newShipTo.state?.name}
                                    postalCode={newShipTo.postalCode}
                                    country={newShipTo.country?.name}
                                    extendedStyles={styles.shipToAddressDisplay}
                                />
                            </GridItem>
                        )}
                        {newRequestedDate && (
                            <GridItem {...styles.billingShippingGridItem}>
                                <SmallHeadingAndText
                                    overridedStyles={styles.newInfoStyles}
                                    heading={translate("New Requested Date")}
                                    text={new Date(newRequestedDate).toLocaleDateString(language?.languageCode)}
                                />
                            </GridItem>
                        )}
                        {newPoNumber && (
                            <GridItem {...styles.billingShippingGridItem}>
                                <SmallHeadingAndText
                                    overridedStyles={styles.newInfoStyles}
                                    heading={translate("New PO Number")}
                                    text={newPoNumber}
                                />
                            </GridItem>
                        )}
                    </GridContainer>
                </Block>
            )}
        </OrderStateContext.Provider>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, null)(ReviewRequestOrderChangeInformation),
    definition: {
        group: constants.widgetGroup.reviewOrderChangeRequest,
        displayName: "Review Change Request Information",
        allowedContexts: ["ReviewOrderChangeRequestPage" as any],
    },
};

export default widgetModule;
